import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import Clear from '@mui/icons-material/Clear';
import Search from '@mui/icons-material/Search';
import {
  SuggestedKeyword,
  SuggestedMarketplace,
  TaskGroup,
  useTaskGroups,
} from '../hooks/useTaskGroups';
import {
  MarketplaceDefinition,
  useMarketplaceDefinitions,
} from '../hooks/useMarketplaceDefinitions';
import {
  assembleMarketplaceOptionLabel,
  filterSuggestedMarketplaces,
  isAddableAssembleMarketplace,
  selectFilteredMarketplaceIds,
} from '../utils/taskGroupAssemble';
import { loadStoreBrandNames, loadStoreProductsForBrand } from '../utils/storeCatalog';

export interface AssembleSubmitPayload {
  brandName: string;
  groupName: string;
  appendToGroupId?: string;
  executionMode: 'extension' | 'internal';
  keywordsText: string;
  maxStage2Products: number;
  marketplaces: SuggestedMarketplace[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  appendToGroup?: TaskGroup | null;
  onSubmit: (payload: AssembleSubmitPayload) => Promise<void>;
  submitting?: boolean;
}

function formatSuccessDate(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
}

function keywordKey(item: SuggestedKeyword): string {
  return item.text.toLowerCase();
}

function platformSelectLabel(allSelected: boolean, filtered: boolean, count: number): string {
  const action = allSelected ? 'Deselect all' : 'Select all';
  return filtered ? `${action} (${count})` : action;
}

export const AssembleGroupDialog: React.FC<Props> = ({
  open,
  onClose,
  appendToGroup,
  onSubmit,
  submitting = false,
}) => {
  const { suggestGroup } = useTaskGroups();
  const { data: allDefinitions = [] } = useMarketplaceDefinitions();
  const [brands, setBrands] = useState<string[]>([]);
  const [brand, setBrand] = useState('');
  const [groupName, setGroupName] = useState('');
  const [maxStage2Products, setMaxStage2Products] = useState(5);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [aiWarning, setAiWarning] = useState<string | null>(null);
  const [keywords, setKeywords] = useState<SuggestedKeyword[]>([]);
  const [selectedKeywordKeys, setSelectedKeywordKeys] = useState<Set<string>>(new Set());
  const [marketplaces, setMarketplaces] = useState<SuggestedMarketplace[]>([]);
  const [selectedDefIds, setSelectedDefIds] = useState<Set<string>>(new Set());
  const [executionMode, setExecutionMode] = useState<'extension' | 'internal'>('extension');
  const [customKeyword, setCustomKeyword] = useState('');
  const [marketplaceQuery, setMarketplaceQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');

  useEffect(() => {
    if (!open) return;
    setBrand(appendToGroup?.brand_name || '');
    setGroupName(appendToGroup?.name || '');
    setMaxStage2Products(5);
    setKeywords([]);
    setMarketplaces([]);
    setSelectedKeywordKeys(new Set());
    setSelectedDefIds(new Set());
    setSuggestError(null);
    setAiWarning(null);
    setCustomKeyword('');
    setMarketplaceQuery('');
    setPlatformFilter('');
    setExecutionMode('extension');
    const load = async () => {
      try {
        setBrands(await loadStoreBrandNames());
      } catch {
        setBrands([]);
      }
    };
    load();
  }, [open, appendToGroup]);

  const handleGenerate = async () => {
    if (!brand) return;
    setLoadingSuggest(true);
    setSuggestError(null);
    setAiWarning(null);
    try {
      const catalog = await loadStoreProductsForBrand(brand);
      const result = await suggestGroup(brand, catalog);
      if (!result) {
        setSuggestError('Could not assemble keywords and platforms.');
        return;
      }
      if (result.ai_error) {
        setAiWarning(`AI keywords unavailable (${result.ai_error}). Using catalog terms.`);
      }
      setKeywords(result.keywords || []);
      setMarketplaces(result.marketplaces || []);
      setSelectedKeywordKeys(
        new Set((result.keywords || []).filter(k => k.kind !== 'bait').map(k => keywordKey(k)))
      );
      setSelectedDefIds(
        new Set(
          (result.marketplaces || []).filter(m => m.selected !== false).map(m => m.definition_id)
        )
      );
      const stage2Defaults = (result.marketplaces || [])
        .filter(m => m.selected !== false)
        .map(m => m.max_stage2_products)
        .filter((n): n is number => typeof n === 'number' && n > 0);
      if (stage2Defaults.length) {
        setMaxStage2Products(Math.max(...stage2Defaults));
      }
      if (!appendToGroup) {
        setGroupName(result.suggested_name || `${brand} keyword scan`);
      }
    } catch (e: any) {
      setSuggestError(e?.message || 'Assemble failed');
    } finally {
      setLoadingSuggest(false);
    }
  };

  const toggleKeyword = (text: string) => {
    const key = text.toLowerCase();
    setSelectedKeywordKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleMarketplace = (id: string) => {
    setSelectedDefIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allKeywordsSelected =
    keywords.length > 0 && keywords.every(k => selectedKeywordKeys.has(keywordKey(k)));
  const visibleMarketplaces = useMemo(
    () => filterSuggestedMarketplaces(marketplaces, platformFilter),
    [marketplaces, platformFilter]
  );
  const platformQuery = platformFilter.trim();
  const allVisiblePlatformsSelected =
    visibleMarketplaces.length > 0 &&
    visibleMarketplaces.every(m => selectedDefIds.has(m.definition_id));

  const toggleAllKeywords = () => {
    if (allKeywordsSelected) {
      setSelectedKeywordKeys(new Set());
    } else {
      setSelectedKeywordKeys(new Set(keywords.map(keywordKey)));
    }
  };

  const toggleAllPlatforms = () => {
    setSelectedDefIds(prev =>
      selectFilteredMarketplaceIds(
        prev,
        visibleMarketplaces.map(m => m.definition_id),
        allVisiblePlatformsSelected
      )
    );
  };

  const addCustomKeyword = () => {
    const text = customKeyword.trim();
    if (!text) return;
    if (!keywords.some(k => k.text.toLowerCase() === text.toLowerCase())) {
      setKeywords(prev => [...prev, { text, source: 'custom', kind: 'product_line' }]);
    }
    setSelectedKeywordKeys(prev => new Set(prev).add(text.toLowerCase()));
    setCustomKeyword('');
  };

  const addCustomMarketplace = (def: MarketplaceDefinition) => {
    if (!def?.id) return;
    setMarketplaces(prev => {
      if (prev.some(m => m.definition_id === def.id)) return prev;
      return [
        ...prev,
        {
          definition_id: def.id,
          name: def.name,
          domain: def.domain,
          last_success_at: def.scrape_last_tested || null,
          scrape_health: def.scrape_health,
          selected: true,
        },
      ];
    });
    setSelectedDefIds(prev => new Set(prev).add(def.id));
    setMarketplaceQuery('');
  };

  const addableDefinitions = useMemo(() => {
    const already = new Set(marketplaces.map(m => m.definition_id));
    return allDefinitions
      .filter(d => isAddableAssembleMarketplace(d, already))
      .slice()
      .sort((a, b) =>
        assembleMarketplaceOptionLabel(a).localeCompare(assembleMarketplaceOptionLabel(b))
      );
  }, [allDefinitions, marketplaces]);

  const selectedKeywords = useMemo(
    () => keywords.filter(k => selectedKeywordKeys.has(keywordKey(k))),
    [keywords, selectedKeywordKeys]
  );
  const selectedMarketplaces = useMemo(
    () => marketplaces.filter(m => selectedDefIds.has(m.definition_id)),
    [marketplaces, selectedDefIds]
  );

  const urlPreviewCount = selectedKeywords.length * selectedMarketplaces.length;

  const canCreate =
    Boolean(brand) &&
    Boolean(groupName.trim()) &&
    selectedKeywords.length > 0 &&
    selectedMarketplaces.length > 0 &&
    !submitting;

  const handleCreate = async () => {
    if (!canCreate) return;
    await onSubmit({
      brandName: brand,
      groupName: groupName.trim(),
      appendToGroupId: appendToGroup?.id,
      executionMode,
      keywordsText: selectedKeywords.map(k => k.text).join('\n'),
      maxStage2Products,
      marketplaces: selectedMarketplaces,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {appendToGroup ? `Assemble into “${appendToGroup.name}”` : 'Assemble group with AI'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', mt: 1, mb: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Brand</InputLabel>
            <Select
              value={brand}
              label="Brand"
              onChange={e => setBrand(String(e.target.value))}
              disabled={Boolean(appendToGroup?.brand_name)}
            >
              <MenuItem value="">Select brand</MenuItem>
              {brands.map(b => (
                <MenuItem key={b} value={b}>
                  {b}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={loadingSuggest ? <CircularProgress size={16} /> : <AutoAwesome />}
            onClick={handleGenerate}
            disabled={!brand || loadingSuggest}
            sx={{ whiteSpace: 'nowrap', minWidth: 160 }}
          >
            Generate
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Searches use infringement-oriented keywords across the catalog, not one query per product
          name. After scraping, Reviews matches each listing to the most likely store product.
        </Typography>

        {suggestError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {suggestError}
          </Alert>
        )}
        {aiWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {aiWarning}
          </Alert>
        )}

        {keywords.length > 0 && (
          <>
            <TextField
              fullWidth
              label="Group name"
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              sx={{ mb: 2 }}
              disabled={Boolean(appendToGroup)}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <FormLabel sx={{ flex: 1 }}>Keywords</FormLabel>
              <Button size="small" onClick={toggleAllKeywords}>
                {allKeywordsSelected ? 'Deselect all' : 'Select all'}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Distinctive product-line terms are on by default. Bait terms (replica / wholesale)
              start off.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {keywords.map(k => {
                const selected = selectedKeywordKeys.has(keywordKey(k));
                return (
                  <Chip
                    key={`${k.kind}:${k.text}`}
                    label={k.kind === 'bait' ? `${k.text} (bait)` : k.text}
                    color={selected ? (k.kind === 'bait' ? 'warning' : 'primary') : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => toggleKeyword(k.text)}
                  />
                );
              })}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Add a keyword"
                value={customKeyword}
                onChange={e => setCustomKeyword(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomKeyword();
                  }
                }}
              />
              <Button onClick={addCustomKeyword} disabled={!customKeyword.trim()}>
                Add
              </Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <FormLabel sx={{ flexShrink: 0 }}>Platforms</FormLabel>
              <TextField
                size="small"
                placeholder="Filter platforms"
                value={platformFilter}
                onChange={e => setPlatformFilter(e.target.value)}
                disabled={!marketplaces.length}
                sx={{ ml: 'auto', width: 240 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: platformFilter ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Clear platform filter"
                        onClick={() => setPlatformFilter('')}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              <Button
                size="small"
                onClick={toggleAllPlatforms}
                disabled={!visibleMarketplaces.length}
              >
                {platformSelectLabel(
                  allVisiblePlatformsSelected,
                  Boolean(platformQuery),
                  visibleMarketplaces.length
                )}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {platformQuery
                ? `${visibleMarketplaces.length} of ${marketplaces.length} match “${platformQuery}”. Select all applies to these.`
                : 'Recent successful runs are listed first. Add any other locale definition below.'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {visibleMarketplaces.map(m => {
                const selected = selectedDefIds.has(m.definition_id);
                const health = m.scrape_health || '';
                const extra =
                  health === 'needs_maintenance'
                    ? 'needs maintenance'
                    : formatSuccessDate(m.last_success_at);
                const label = extra
                  ? `${m.name || m.domain} · ${extra}`
                  : m.name || m.domain || m.definition_id;
                return (
                  <Chip
                    key={m.definition_id}
                    label={label}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => toggleMarketplace(m.definition_id)}
                  />
                );
              })}
            </Box>
            <Autocomplete
              disablePortal
              autoHighlight
              options={addableDefinitions}
              value={null}
              inputValue={marketplaceQuery}
              onInputChange={(_, value) => setMarketplaceQuery(value)}
              onChange={(_, value) => {
                if (value) addCustomMarketplace(value);
              }}
              getOptionLabel={assembleMarketplaceOptionLabel}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              sx={{ mb: 2 }}
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Add a marketplace"
                  helperText="Inactive locales are allowed as an override"
                />
              )}
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
              <FormControl>
                <FormLabel>Run mode</FormLabel>
                <RadioGroup
                  row
                  value={executionMode}
                  onChange={e => setExecutionMode(e.target.value as 'extension' | 'internal')}
                >
                  <FormControlLabel
                    value="extension"
                    control={<Radio />}
                    label="Chrome extension"
                  />
                  <FormControlLabel
                    value="internal"
                    control={<Radio />}
                    label="Internal (cluster)"
                  />
                </RadioGroup>
              </FormControl>
              <TextField
                label="Max Stage 2 Products"
                type="number"
                value={maxStage2Products}
                onChange={e => setMaxStage2Products(Math.max(0, parseInt(e.target.value, 10) || 0))}
                inputProps={{ min: 0, max: 200 }}
                helperText="Full product pages per task (0 = search only)"
                sx={{ minWidth: 220 }}
              />
            </Box>

            <Typography variant="body2" color="text.secondary">
              {selectedMarketplaces.length} platform
              {selectedMarketplaces.length === 1 ? '' : 's'} × {selectedKeywords.length} search
              {selectedKeywords.length === 1 ? '' : 'es'} = {urlPreviewCount} URL
              {urlPreviewCount === 1 ? '' : 's'}. Tasks are created paused, one per platform.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={!canCreate}>
          {submitting ? 'Creating…' : appendToGroup ? 'Add tasks to group' : 'Create paused tasks'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssembleGroupDialog;
